import { AfterViewInit, Component, EventEmitter, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { merge, Observable, of as observableOf, of, throwError } from 'rxjs';
import { catchError, delay, first, map, startWith, switchMap, tap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';

import { AuthorizeService } from '../../api-authorization/authorize.service';
import { ILabelRow, LabelFilters, LabelsService } from '../labels.service';
import { ISupplier, SupplierSection, SupplierService } from '../supplier.service';
import { Role } from '../_models/role';
import { FormControl } from '@angular/forms';
import { UserService } from '../user.service';
import { DatePipe } from '@angular/common';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { AutocompleteService, IFust } from '../autocomplete.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

@Component({
    selector: 'app-labels',
    templateUrl: './labels.component.html',
    styleUrls: ['./labels.component.css'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
    ],
})
export class LabelsComponent implements OnInit, AfterViewInit {
    _baseUrl: string;
    isAdmin: Observable<boolean>;

    data: ILabelRow[] = [];
    filteredData: ILabelRow[];

    control: FormControl = new FormControl('');
    suppliers: ISupplier[] = [];
    filteredSuppliers: Observable<ISupplier[]>;

    supplierAssignedIdcontrol: FormControl = new FormControl('');
    kwekerartikelnrs: string[] = [];
    filteredSupplierAssignedIds: Observable<string[]>;

    potmaatcontrol: FormControl = new FormControl('');
    potmaten: number[] = [];
    filteredPotmaten: Observable<string[]>;

    selectedEan: number;

    dataPrinted: ILabelRow[];
    dataUnPrinted: ILabelRow[];

    isLoadingRowResults = true;
    isLoadingSupplierResults = true;

    numberOfLabels: number = 0;
    isAllPrinted: boolean;
    saveButtonIsEnabled: boolean;
    filters: LabelFilters;
    selectedWeergave: number = 0;

    labelsFilter: EventEmitter<any> = new EventEmitter();

    @ViewChild(MatSort, { static: false }) sort: MatSort;
    selectedTabIndex: number = 0;
    unprintedSelection: ILabelRow[] = [];
    printedSelection: ILabelRow[] = [];

    constructor(
        private authorizeService: AuthorizeService,
        private _supplierService: SupplierService,
        private _autocompleteService: AutocompleteService,
        private _labelsService: LabelsService,
        private _userService: UserService,
        @Inject('BASE_URL') baseUrl: string) {
        this._baseUrl = baseUrl;
    }

    ngOnInit() {
        this.isAdmin = this.authorizeService.getUser().pipe(map(u => u && u.role && (u.role.includes(Role.Developer) || u.role.includes(Role.Administrator))));

        this.isAdmin.subscribe(data => data ? this.control.enable() : this.control.disable());

        this.filteredSuppliers = this.control.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || '')),
        );
        this._supplierService.getSuppliers(SupplierSection.regels).subscribe(data => {
            this.suppliers = data.items;

            var selectedSupplier = this.suppliers.find(function (element) {
                return element.selected;
            });
            if (selectedSupplier) {
                this.selectedEan = selectedSupplier.ean;
                this.control.patchValue(selectedSupplier.name);

                this.setAutocompletes();
            }

            this.isLoadingSupplierResults = false;
        });

        this.filters = new LabelFilters();

        this.filteredSupplierAssignedIds = this.supplierAssignedIdcontrol.valueChanges.pipe(
            startWith(''),
            map(value => this._filterSupplierAssignedIds(value || '')),
        );

        this.filteredPotmaten = this.potmaatcontrol.valueChanges.pipe(
            startWith(''),
            map(value => this._filterPotmaten(value || '')),
        );
    }

    ngAfterViewInit() {
        merge(this.labelsFilter)
            .pipe(
                startWith({}),
                delay(0),
                switchMap(() => {
                    this.isLoadingRowResults = true;
                    return this._labelsService.getLabelsForSupplier(this.selectedWeergave, this.filters, this.selectedEan);
                }),
                map(data => {
                    // Flip flag to show that loading has finished.
                    this.isLoadingRowResults = false;
                    return data ? data.items : [];
                }),
                catchError(() => {
                    this.isLoadingRowResults = false;
                    // Return empty data.
                    return observableOf([]);
                })
            ).subscribe(data => {
                this.data = data;
                this.filterData();

                this.dataPrinted = this.filteredData.filter(r => r.labelIsCreated);
                this.dataUnPrinted = this.filteredData.filter(r => !r.labelIsCreated);

                this.SetNumberOfLabels();
                this.isAllPrinted = this.data.every(row => row.labelIsCreated);
            });
    }

    setAutocompletes() {
        this.kwekerartikelnrs = [];
        this.potmaten = [];

        this._autocompleteService.getKwekerArtikelNrs('', this.selectedEan).subscribe((data: string[]) => {
            if (data.length) {
                this.kwekerartikelnrs = data;
            }
            this.supplierAssignedIdcontrol.enable();
        });

        this._autocompleteService.getPotmaten('', this.selectedEan).subscribe((data: number[]) => {
            if (data.length) {
                this.potmaten = data;
            }
            this.potmaatcontrol.enable();
        });
    }

    private SetNumberOfLabels() {
        this.numberOfLabels = this.filteredData.length;
    }

    filterBySearchTerm() {
        this.filterData();

        this.dataPrinted = this.filteredData.filter(r => r.labelIsCreated);
        this.dataUnPrinted = this.filteredData.filter(r => !r.labelIsCreated);
    }

    private filterData() {
        let datePipe = new DatePipe('en-US');

        if (!(this.filters.searchTerm && this.filters.searchTerm.trim())) {
            this.filteredData = this.data;
        }
        else {
            this.filteredData = this.data.filter(row => {
                let searchTerm = this.filters.searchTerm?.toLowerCase();
                return (row.orderNr || '').toLowerCase().indexOf(searchTerm) > -1 ||
                    (row.debiteurNrs || '').indexOf(searchTerm) > -1 ||
                    (row.klantArtikelOmschrijving || '').toLowerCase().indexOf(searchTerm) > -1 ||
                    (row.potmaat || '').toString().indexOf(searchTerm) > -1 ||
                    (row.barCode || '').toLowerCase().indexOf(searchTerm) > -1 ||
                    (row.datumAangemaakt ? datePipe.transform(new Date(row.datumAangemaakt), 'dd-MM-yyyy HH:mm') : '').toString().indexOf(searchTerm) > -1 ||
                    (row.leverDatumTijdstip ? datePipe.transform(new Date(row.leverDatumTijdstip), 'dd-MM-yyyy HH:mm') : '').toString().indexOf(searchTerm) > -1;
            });
        }

        this.SetNumberOfLabels();
    }

    private _filterPotmaten(value: string): string[] {
        const filterValue = this._normalizeValue(value);
        return this.potmaten.filter(k => this._normalizeValue(k.toString()).includes(filterValue)).map(function (e) { return e.toString() });
    }

    private _filterSupplierAssignedIds(value: string): string[] {
        const filterValue = this._normalizeValue(value);
        return this.kwekerartikelnrs.filter(k => this._normalizeValue(k).includes(filterValue));
    }

    private _filter(value: string): ISupplier[] {
        const filterValue = this._normalizeValue(value);
        return this.suppliers.filter(supplier => this._normalizeValue(supplier.name).includes(filterValue) || supplier.ean.toString() == filterValue);
    }

    private _normalizeValue(value: string): string {
        return value.toLowerCase().replace(/\s/g, '');
    }

    printPrintedLabels() {
        this.openPdf(this.printedSelection.map(r => r.orderNr));
        this.printedSelection = [];
    }

    printUnPrintedLabels() {
        this.openPdf(this.unprintedSelection.map(r => r.orderNr));
        this.unprintedSelection = [];
    }

    markAsUnprinted() {
        this._labelsService.markAsUnprinted(this.printedSelection.map(r => r.orderNr)).subscribe(
            () => {
                this.printedSelection = [];
                this.refresh();
            }
        );
    }

    openPdf(orderNrs: string[]) {
        let currentUser: any;
        combineLatest(
            this._userService.getCurrentUser().pipe(
                tap(userResult => currentUser = userResult)
            ),
            this._labelsService.downloadPDF(orderNrs)
        ).pipe(
            first(),//makes sure the subscribtion get cleaned after first result
            tap(([user, fileResponse]) => {
                var fileURL = window.URL.createObjectURL(fileResponse);
                var newWindow = window.open(fileURL);
                if (newWindow) {
                    newWindow.focus();
                    newWindow.print();
                }
                this.refresh();
            }),
        ).subscribe();
    }

    refresh() {
        this.saveButtonIsEnabled = false;
        this.labelsFilter.emit();
    }

    onEnterSupplierAssignedId(evt: any) {
        const selectedSupplierAssignedId = this.kwekerartikelnrs.find(k =>
            k.toLowerCase() == evt.source.value.toLowerCase());
        if (evt.source.selected) {
            if (selectedSupplierAssignedId) {
                this.filters.kwekerartikelnr = selectedSupplierAssignedId;
                this.supplierAssignedIdcontrol.patchValue(selectedSupplierAssignedId, { emitEvent: false });

                this.refresh();
            }
        }
    }

    onEnterPotmaat(evt: any) {
        const selectedPotmaat = this.potmaten.find(k => k == evt.source.value);
        if (evt.source.selected) {
            if (selectedPotmaat) {
                this.filters.potmaat = selectedPotmaat.toString();
                this.potmaatcontrol.patchValue(selectedPotmaat, { emitEvent: false });

                this.refresh();
            }
        }
    }

    onEnter(evt: any) {
        const selectedSupplier = this.suppliers.find(supplier =>
            supplier.name.toLowerCase() == evt.source.value.toLowerCase());
        if (evt.source.selected) {
            // set value to control because we just resetted the control
            //this.control.patchValue(evt.source.value);

            if (selectedSupplier) {
                this.selectedEan = selectedSupplier.ean;
                this.control.patchValue(selectedSupplier.name);

                this.setAutocompletes();

                this.doResetFilters(false);

                this.refresh();
            }
        }
    }

    doResetFilters(withRefresh: boolean) {
        this.filters = new LabelFilters();

        //this.control.patchValue(null);
        this.potmaatcontrol.patchValue(null);
        this.supplierAssignedIdcontrol.patchValue(null);

        if (withRefresh) {
            this.refresh();
        }
    }

    doResetSearchTerm() {
        this.filters.searchTerm = '';
        this.refresh();
    }

    onTabChange(event: MatTabChangeEvent) {
        this.selectedTabIndex = event.index;
    }

    onSelectionChangeUnprinted(selectedItems: ILabelRow[]) {
        this.unprintedSelection = selectedItems;
    }

    onSelectionChangePrinted(selectedItems: ILabelRow[]) {
        this.printedSelection = selectedItems;
    }
}